import React, { useState } from "react";

interface DatePriceGridProps {
    selectedDate: string;
    getLowestFare: (date: string) => number | null;
    origin: string;
    destination: string;
    returnDate?: string;
}

const DatePriceGrid: React.FC<DatePriceGridProps> = ({
    selectedDate,
    getLowestFare,
    origin,
    destination,
    returnDate,
}) => {
    const [showDates, setShowDates] = useState(true);
    // Format date for header display
    const formatHeaderDate = (dateStr: string) => {
        // Parse the date string properly to avoid timezone issues
        const [year, month, day] = dateStr.split("-").map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        const options: Intl.DateTimeFormatOptions = {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
        };
        return date.toLocaleDateString("en-US", options);
    };

    // Generate dates around the selected date
    const generateDates = () => {
        const dates = [];
        // Parse the selectedDate properly
        const [year, month, day] = selectedDate.split("-").map(Number);
        const baseDate = new Date(year, month - 1, day); // month is 0-indexed in Date constructor

        // Show 3 days before and 3 days after
        for (let i = -3; i <= 3; i++) {
            const date = new Date(year, month - 1, day + i);
            dates.push(date);
        }

        return dates;
    };

    const dates = generateDates();

    const formatDateDisplay = (date: Date) => {
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        return {
            day: days[date.getDay()],
            date: date.getDate(),
            dayDate: `${days[date.getDay()]} ${date.getDate()}`, // e.g., "Sun 14"
        };
    };

    const getDateString = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    return (
        <>
            <div className="relative max-w-[1200px] mx-auto py-3 -mt-30">
                <div className="bg-white rounded-lg shadow-lg">
                    <div className="px-6">
                        {/* Flight type header */}
                        <div className="py-6">
                            <h1 className="text-lg font-bold text-gray-900 text-center">
                                Departure flight{" "}
                                <span className="text-[#0047ba]">{origin}</span>{" "}
                                to{" "}
                                <span className="text-[#0047ba]">
                                    {destination}
                                </span>
                            </h1>
                            <p className="text-center text-[#0047ba] mt-2 font-bold">
                                {formatHeaderDate(selectedDate)}
                            </p>
                        </div>

                        {/* Date price grid */}
                        {showDates && (
                            <div className="pb-4">
                                <div className="flex justify-center gap-3 px-4">
                                    {dates.map((date) => {
                                        const dateString = getDateString(date);
                                        const display = formatDateDisplay(date);
                                        const lowestFare =
                                            getLowestFare(dateString);
                                        const isSelected =
                                            dateString === selectedDate ||
                                            dateString === returnDate;
                                        const hasFlights = lowestFare !== null;

                                        return (
                                            <div
                                                key={dateString}
                                                data-date={dateString}
                                                className="flex flex-col items-center"
                                            >
                                                <div
                                                    className={`relative flex flex-col justify-end rounded-lg shadow-lg ${
                                                        isSelected
                                                            ? "bg-[#0047ba] text-white rounded-b-none"
                                                            : hasFlights
                                                            ? "bg-gray-50 text-gray-700"
                                                            : "bg-[#e0dfe7] text-gray-600"
                                                    }`}
                                                    style={{
                                                        width: "100px",
                                                        height: "50px",
                                                        padding: "5px 10px",
                                                    }}
                                                >
                                                    {/* Price tag icon for selected */}
                                                    {isSelected &&
                                                        hasFlights && (
                                                            <div className="absolute top-1 right-1">
                                                                <svg
                                                                    width="14"
                                                                    height="14"
                                                                    viewBox="0 0 24 24"
                                                                    fill="#FFC107"
                                                                >
                                                                    <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
                                                                </svg>
                                                            </div>
                                                        )}
                                                    {hasFlights ? (
                                                        <div className="flex flex-col items-center">
                                                            <div className="text-[12px] font-semibold leading-none">
                                                                USD
                                                            </div>
                                                            <div className="text-[12px] font-semibold mt-0.5">
                                                                {lowestFare.toLocaleString(
                                                                    "en-US",
                                                                    {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    }
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-[14px] font-medium text-center text-black">
                                                            No flights
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 mt-1">
                                                    {isSelected && (
                                                        <div className="w-3 h-3 rounded-full bg-[#0047ba] flex items-center justify-center">
                                                            <svg
                                                                width="4"
                                                                height="4"
                                                                viewBox="0 0 10 10"
                                                                fill="none"
                                                            >
                                                                <path
                                                                    d="M1 5L3.5 7.5L9 2"
                                                                    stroke="white"
                                                                    strokeWidth="3"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    )}
                                                    <div
                                                        className={`text-sm ${
                                                            isSelected
                                                                ? "font-medium text-gray-700"
                                                                : "text-gray-600"
                                                        }`}
                                                    >
                                                        {display.dayDate}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Hide/Show dates button */}
            <div className="flex justify-center py-4">
                <button
                    onClick={() => setShowDates(!showDates)}
                    className="px-6 py-2 bg-white rounded-full text-gray-700 text-sm font-medium flex items-center gap-2 transition-colors shadow-md -mt-12 relative z-10"
                >
                    <span className="text-lg">{showDates ? "⌃" : "⌄"}</span>
                    {showDates ? "Hide dates" : "Show dates"}
                </button>
            </div>
        </>
    );
};

export default DatePriceGrid;
